import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const CheckInPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [reservationInfo, setReservationInfo] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchReservationInfo();
  }, [token]);

  const fetchReservationInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reservations/checkin/token/${token}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to load reservation');
        return;
      }

      setReservationInfo(data.reservation);
      setCheckInStatus(data.checkInStatus);
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/reservations/checkin/token/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to check in');
        return;
      }

      setSuccess(true);
      setReservationInfo(prev => ({
        ...prev,
        status: 'checked_in',
        checkedInAt: data.reservation.checkedInAt
      }));
      setCheckInStatus(prev => ({
        ...prev,
        isCheckedIn: true,
        canCheckIn: false
      }));
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setCheckingIn(false);
    }
  };

  const formatTime = (isoString, timezone) => {
    if (!isoString) return '';
    const date = parseISO(isoString);
    return formatInTimeZone(date, timezone || 'America/Los_Angeles', 'h:mm a');
  };

  const formatDate = (isoString, timezone) => {
    if (!isoString) return '';
    const date = parseISO(isoString);
    return formatInTimeZone(date, timezone || 'America/Los_Angeles', 'EEEE, MMMM d, yyyy');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reservation...</p>
        </div>
      </div>
    );
  }

  if (error && !reservationInfo) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Check-in Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-blue-600">BoardRez</h1>
          <p className="text-gray-500 text-sm">Board Reservation Check-in</p>
        </div>

        {/* Success State */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-green-800">Checked In!</h3>
                <p className="text-sm text-green-600">Enjoy your session!</p>
              </div>
            </div>
          </div>
        )}

        {/* Already Checked In State */}
        {checkInStatus?.isCheckedIn && !success && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-blue-800">Already Checked In</h3>
                <p className="text-sm text-blue-600">
                  at {formatTime(reservationInfo?.checkedInAt, reservationInfo?.timezone)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Too Early State */}
        {checkInStatus?.isTooEarly && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-yellow-800">Too Early</h3>
                <p className="text-sm text-yellow-600">
                  Check-in opens at {formatTime(checkInStatus.checkInWindowStart, reservationInfo?.timezone)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Expired State */}
        {checkInStatus?.isExpired && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-red-800">Reservation Expired</h3>
                <p className="text-sm text-red-600">This time slot has ended</p>
              </div>
            </div>
          </div>
        )}

        {/* Reservation Details */}
        {reservationInfo && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Reservation Details
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium text-gray-900">{reservationInfo.userName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gym:</span>
                <span className="font-medium text-gray-900">{reservationInfo.gymName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Board:</span>
                <span className="font-medium text-gray-900">{reservationInfo.boardName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium text-gray-900">
                  {formatDate(reservationInfo.startTime, reservationInfo.timezone)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time:</span>
                <span className="font-medium text-gray-900">
                  {formatTime(reservationInfo.startTime, reservationInfo.timezone)} - {formatTime(reservationInfo.endTime, reservationInfo.timezone)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Check-in Button */}
        {checkInStatus?.canCheckIn && !success && (
          <button
            onClick={handleCheckIn}
            disabled={checkingIn}
            className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {checkingIn ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Checking in...
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Check In Now
              </>
            )}
          </button>
        )}

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <Link
            to="/login"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Sign in for more options
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckInPage;
